import { useState, useEffect } from "react";
import { createPortal } from "react-dom";

// Thumbnail that opens a full-screen, click-to-enlarge view. Preserves the image's
// natural orientation (object-contain) in both the thumbnail and the enlarged view.
// The overlay is portaled to <body> so it can't be trapped by a transformed/animated
// ancestor (which would otherwise re-scope `position: fixed` and push it off-screen).
export default function ImageZoom({ src, alt = "", className = "", testid }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!src) return null;
  return (
    <>
      <img
        src={src}
        alt={alt}
        data-testid={testid}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={`cursor-zoom-in ${className}`}
      />
      {open && createPortal(
        <div
          className="fixed inset-0 z-[2000] bg-ink/90 flex items-center justify-center p-4 cursor-zoom-out"
          data-testid="image-zoom-overlay"
          onClick={() => setOpen(false)}
        >
          <button
            data-testid="image-zoom-close"
            onClick={(e) => { e.stopPropagation(); setOpen(false); }}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-cream/20 text-cream text-2xl leading-none flex items-center justify-center hover:bg-cream/30"
            aria-label="Close"
          >
            ×
          </button>
          <img
            src={src}
            alt={alt}
            className="max-w-[92vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>,
        document.body
      )}
    </>
  );
}
